// src/services/dailyExecution.ts
//
// Deterministic daily planning. Ranks what to work on next using only concrete
// signals (due dates, priority, project/milestone/goal risk, blocked state,
// proof-required, estimated duration). Every item carries human-readable
// reasons. No AI, no generated text.

import type { Task, Project, Goal } from '../types/api';

export type FocusUrgency = 'overdue' | 'today' | 'soon' | 'blocked' | 'normal';

export interface FocusItem {
  task: Task;
  score: number;
  reasons: string[];
  urgency: FocusUrgency;
  /** Optional minutes estimate, surfaced for planning. */
  estimatedMinutes: number | null;
}

export interface DeriveDailyFocusParams {
  tasks: Task[];
  projects: Project[];
  goals: Goal[];
  /** Accepted for schedule-aware extensions; not used in ranking yet. */
  events?: unknown[];
  today: string;
  /** Only include tasks assigned to this user or unassigned. */
  forUserId?: string | null;
  max?: number;
}

const PRIORITY_WEIGHT: Record<string, number> = { urgent: 40, high: 25, medium: 10, low: 0 };

function dayDiff(fromStr: string, toStr: string): number {
  const a = new Date(`${fromStr}T00:00:00`).getTime();
  const b = new Date(`${toStr}T00:00:00`).getTime();
  return Math.round((b - a) / 86_400_000);
}

export function deriveDailyFocus({
  tasks,
  projects,
  goals,
  today,
  forUserId,
  max = 7,
}: DeriveDailyFocusParams): FocusItem[] {
  // Risk sets (deterministic) derived from already-computed progress.
  const riskyProjects = new Set<string>();
  const riskyMilestones = new Set<string>();
  for (const p of projects) {
    const pe = p.executionSummary;
    if (p.progress?.trackingStatus === 'behind' || (pe && (pe.overdueTasks > 0 || pe.blockedTasks > 0))) {
      riskyProjects.add(p.id);
    }
    for (const m of p.milestones ?? []) {
      const me = m.executionSummary;
      if (m.progress?.trackingStatus === 'behind' || (me && (me.overdueTasks > 0 || me.blockedTasks > 0))) {
        riskyMilestones.add(m.id);
      }
    }
  }
  const behindGoals = new Set(goals.filter((g) => g.progress?.trackingStatus === 'behind').map((g) => g.id));

  const soonStr = (() => {
    const d = new Date(`${today}T00:00:00`);
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  })();

  const candidates = tasks.filter((t) => {
    if (t.status === 'done') return false;
    if (forUserId && t.assigneeUserId && t.assigneeUserId !== forUserId) return false;
    return true;
  });

  const items: FocusItem[] = candidates.map((t) => {
    const reasons: string[] = [];
    let score = 0;
    let urgency: FocusUrgency = 'normal';
    const due = t.dueDate ? t.dueDate.split('T')[0] : null;

    if (due && due < today) {
      const days = Math.max(1, dayDiff(due, today));
      score += 100 + Math.min(days, 30);
      reasons.push(days === 1 ? 'Overdue by 1 day' : `Overdue by ${days} days`);
      urgency = 'overdue';
    } else if (due && due === today) {
      score += 70;
      reasons.push('Due today');
      urgency = 'today';
    } else if (due && due <= soonStr) {
      score += 45;
      reasons.push('Due within 48h');
      urgency = 'soon';
    }

    score += PRIORITY_WEIGHT[t.priority] ?? 0;
    if (t.priority === 'urgent') reasons.push('Urgent priority');
    else if (t.priority === 'high') reasons.push('High priority');

    if (t.status === 'in_progress') {
      score += 15;
      reasons.push('Already in progress');
    }
    if (t.status === 'blocked') {
      score += 35;
      reasons.push(t.blockerReason ? `Blocked: ${t.blockerReason}` : 'Blocked — needs unblocking');
      if (urgency === 'normal') urgency = 'blocked';
    }

    if (t.milestoneId && riskyMilestones.has(t.milestoneId)) {
      score += 30;
      reasons.push('On an at-risk milestone');
    } else if (t.projectId && riskyProjects.has(t.projectId)) {
      score += 25;
      reasons.push('On an at-risk project');
    }
    if (t.goalId && behindGoals.has(t.goalId)) {
      score += 20;
      reasons.push('Supports a goal that is behind');
    }
    if (t.proofRequired && t.status !== 'done') {
      score += 10;
      reasons.push('Proof required to complete');
    }
    // Estimated duration: small known tasks get a slight quick-win nudge.
    if (typeof t.estimatedMinutes === 'number' && t.estimatedMinutes > 0 && t.estimatedMinutes <= 30) {
      score += 5;
      reasons.push('Quick win (≤30m)');
    }

    return { task: t, score, reasons, urgency, estimatedMinutes: t.estimatedMinutes ?? null };
  });

  return items
    .filter(
      (f) =>
        f.urgency !== 'normal' ||
        f.task.priority === 'urgent' ||
        f.task.priority === 'high' ||
        !!f.task.proofRequired ||
        (f.task.milestoneId ? riskyMilestones.has(f.task.milestoneId) : false) ||
        (f.task.projectId ? riskyProjects.has(f.task.projectId) : false) ||
        (f.task.goalId ? behindGoals.has(f.task.goalId) : false),
    )
    .sort((a, b) => b.score - a.score)
    .slice(0, max);
}
